import React, { useEffect, useRef, useState } from "react";
import { IonButton, IonModal } from "@ionic/react";
import { useSmsVerificationData } from "../../providers/smsVerifivation";
import styles from "./AuthByCode.module.scss";
import classNames from "classnames/bind";
import { AuthBySmsOtpInput } from "../AuthBySmsOtpInput/AuthBySmsOtpInput";
import { useTranslation } from "react-i18next";

type AuthByCodeProps = {
  isOpen: boolean;
  isCodeValid: boolean;
  toggleIsOpen: () => void;
  auth: (code: string) => void;
  requestNewCode: () => void;
};

const cx = classNames.bind(styles);

const AuthByCode = ({ isOpen, toggleIsOpen, auth, requestNewCode, isCodeValid }: AuthByCodeProps) => {
  const { t } = useTranslation();
  const { canGetNewCode, waitingRestTime } = useSmsVerificationData();
  const [code, setCode] = useState("");
  const firstRequest = useRef(true);

  const cancelModal = () => {
    toggleIsOpen();
  };

  const getNewCode = () => {
    requestNewCode();
  };

  const handleForm = (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (code.length < 4) return;
    auth(code);
  };

  useEffect(() => {
    if (!isOpen) {
      setCode("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isCodeValid && isOpen) {
      setCode("");
    }
  }, [isCodeValid, isOpen]);

  useEffect(() => {
    if (code.length === 4 && firstRequest.current) {
      firstRequest.current = false;
      auth(code);
    }
  }, [code]);

  return (
    <IonModal id="auth-code-modal" backdropDismiss={false} className={cx("auth-code-modal")} isOpen={isOpen}>
      <div className={cx("head")}>
        <h2 className={cx("head__title")}>{t("Enter the code from the SMS message")}</h2>
      </div>
      <div className={cx("message")}>
        {t("If the code is not received, check the entered phone number and try again")}
      </div>
      <form onSubmit={handleForm}>
        <AuthBySmsOtpInput isValid={isCodeValid} otp={code} setOtp={(value) => setCode(value)} />
        <div className={cx("buttons")}>
          <IonButton type="button" className={cx("buttons__item", "cancel")} onClick={cancelModal}>
            <span className="alert-button-inner sc-ion-alert-ios">{t("Close")}</span>
          </IonButton>
          <IonButton
            onClick={getNewCode}
            disabled={!canGetNewCode}
            type="button"
            className={cx("buttons__item", "try-again", { disabled: !canGetNewCode })}
          >
            <span className={cx("try-again__span")}>
              {t("Send again")}
              <span className={cx("try-again__timer")}>{!canGetNewCode ? `(${waitingRestTime})` : ""}</span>
            </span>
          </IonButton>
          <input type="submit" hidden />
          <IonButton disabled={code.length !== 4} type="submit" className={cx("buttons__item", "continue")}>
            <span className="alert-button-inner sc-ion-alert-ios">{t("Continue")}</span>
          </IonButton>
        </div>
      </form>
    </IonModal>
  );
};

export { AuthByCode };
